import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, ArrowUpCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { logger } from '@/lib/logger';

interface NorthReport {
  id: string;
  address: string;
  city: string;
  state: string;
  published_at: string;
}

interface OverLimitDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentCount: number;
  maxAllowed: number;
  onUnpublishComplete: () => void;
  requireAtLeastOne?: boolean; // If true, requires unpublishing at least 1 even if at limit
}

export function OverLimitDialog({
  open,
  onOpenChange,
  currentCount,
  maxAllowed,
  onUnpublishComplete,
  requireAtLeastOne = false,
}: OverLimitDialogProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [publishedNorths, setPublishedNorths] = useState<NorthReport[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);

  // Calculate how many reports need to be unpublished
  // If requireAtLeastOne is true (trying to publish a new one), they need to make room for 1 more
  // So if currentCount >= maxAllowed, they need to unpublish at least 1
  const overLimitCount = requireAtLeastOne
    ? (currentCount >= maxAllowed ? Math.max(1, currentCount - maxAllowed + 1) : 1)
    : Math.max(1, currentCount - maxAllowed);
  const selectedCount = selectedIds.size;
  const canProceed = selectedCount >= overLimitCount;

  useEffect(() => {
    if (open) {
      loadPublishedNorths();
    }
  }, [open]);

  const loadPublishedNorths = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get user's team
      const { data: profile } = await supabase
        .from('profiles')
        .select('team_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.team_id) return;

      // Get all team members
      const { data: teamProfiles } = await supabase
        .from('profiles')
        .select('user_id')
        .eq('team_id', profile.team_id);

      const teamUserIds = teamProfiles?.map(p => p.user_id) || [];

      // Get all published norths for the team, ordered by oldest first
      const { data: norths, error } = await supabase
        .from('norths')
        .select('id, address, city, state, published_at')
        .in('user_id', teamUserIds)
        .eq('is_published', true)
        .eq('is_draft', false)
        .order('published_at', { ascending: true });

      if (error) throw error;

      setPublishedNorths(norths || []);
    } catch (error) {
      logger.error('Error loading published norths:', error);
      toast({
        title: 'Error',
        description: 'Failed to load your North Reports',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleUnpublish = async () => {
    if (!canProceed) return;

    setUnpublishing(true);
    try {
      // Unpublish selected norths
      const { error } = await supabase
        .from('norths')
        .update({
          is_published: false,
          published_at: null,
        })
        .in('id', Array.from(selectedIds));

      if (error) throw error;

      toast({
        title: 'Reports Unpublished',
        description: `Successfully unpublished ${selectedCount} North Report${selectedCount > 1 ? 's' : ''}. You're now within your plan limits.`,
      });

      setSelectedIds(new Set());
      onUnpublishComplete();
      onOpenChange(false);
    } catch (error) {
      logger.error('Error unpublishing norths:', error);
      toast({
        title: 'Error',
        description: 'Failed to unpublish reports. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUnpublishing(false);
    }
  };

  const handleUpgrade = () => {
    onOpenChange(false);
    navigate('/settings?tab=billing');
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    // Check for invalid or epoch dates (Dec 31, 1969 / Jan 1, 1970)
    if (isNaN(date.getTime()) || date.getFullYear() < 2000) {
      return 'Unknown date';
    }
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-6 w-6" />
            Over Plan Limit - Action Required
          </DialogTitle>
          <DialogDescription className="text-base">
            Your plan allows <span className="font-semibold">{maxAllowed}</span> active North Report{maxAllowed !== 1 ? 's' : ''},
            and you currently have <span className="font-semibold">{currentCount} active</span>.
            {requireAtLeastOne ? (
              <> To publish a new report, please select at least <span className="font-semibold text-red-600">{overLimitCount}</span> report{overLimitCount > 1 ? 's' : ''} to unpublish.</>
            ) : (
              <> Please select at least <span className="font-semibold text-red-600">{overLimitCount}</span> report{overLimitCount > 1 ? 's' : ''} to unpublish.</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col min-h-0 mt-4">
          {/* Selection Counter */}
          <div className="flex items-center justify-between mb-4 p-3 bg-muted rounded-lg">
            <span className="text-sm font-medium">
              Selected: <span className={selectedCount >= overLimitCount ? 'text-green-600' : 'text-red-600'}>{selectedCount}</span> / {overLimitCount} required
            </span>
            {canProceed && (
              <Badge variant="default" className="bg-green-600">
                Ready to proceed
              </Badge>
            )}
          </div>

          {/* Report List */}
          <div className="flex-1 overflow-y-auto border rounded-lg">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">
                Loading your North Reports...
              </div>
            ) : publishedNorths.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                No published reports found.
              </div>
            ) : (
              <div className="divide-y">
                {publishedNorths.map((north) => (
                  <label
                    key={north.id}
                    className="flex items-center gap-3 p-3 hover:bg-muted/50 cursor-pointer transition-colors"
                  >
                    <Checkbox
                      checked={selectedIds.has(north.id)}
                      onCheckedChange={() => toggleSelection(north.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {north.address}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {north.city}, {north.state}
                      </p>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Published {formatDate(north.published_at)}
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="mt-6 flex-col sm:flex-row gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={unpublishing}
          >
            Cancel
          </Button>
          <div className="flex gap-2 flex-1 sm:flex-initial">
            <Button
              variant="outline"
              onClick={handleUpgrade}
              disabled={unpublishing}
              className="flex items-center gap-2 flex-1 sm:flex-initial"
            >
              <ArrowUpCircle className="h-4 w-4" />
              Upgrade Plan
            </Button>
            <Button
              onClick={handleUnpublish}
              disabled={!canProceed || unpublishing}
              className="bg-red-600 hover:bg-red-700 flex-1 sm:flex-initial"
            >
              {unpublishing
                ? 'Unpublishing...'
                : canProceed
                ? `Unpublish ${selectedCount} Report${selectedCount > 1 ? 's' : ''} and Continue`
                : `Select ${overLimitCount} Report${overLimitCount > 1 ? 's' : ''} to Continue`}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
